import React from 'react';
import { Box, Typography, Button, Paper, IconButton, Tooltip, ButtonBase, Chip, Card, CardContent, CardMedia } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const items = [
  { 
    title: 'AI-Powered Sentiment Analysis for Gaming', 
    desc: 'Implemented real-time player sentiment analysis to improve game experience and retention rates by 35%',
    industry: 'Gaming',
    result: '35% increase in retention',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Predictive Analytics Platform for Retail', 
    desc: 'Built a machine learning solution that predicts inventory needs, reducing costs by 28% and stockouts by 45%',
    industry: 'Retail',
    result: '28% cost reduction',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Intelligent Document Processing for Healthcare', 
    desc: 'Automated medical records processing using OCR and NLP, reducing manual effort by 70% and improving accuracy',
    industry: 'Healthcare',
    result: '70% efficiency gain',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Fraud Detection System for FinTech', 
    desc: 'Developed ML-based fraud detection reducing false positives by 60% while catching 95% of fraudulent transactions',
    industry: 'FinTech',
    result: '95% fraud detection',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Customer Churn Prediction for Telecom', 
    desc: 'Machine learning model identifying at-risk customers with 89% accuracy, enabling proactive retention strategies',
    industry: 'Telecom',
    result: '89% prediction accuracy',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Supply Chain Optimization with AI', 
    desc: 'AI-powered route optimization and demand forecasting reducing logistics costs by 32% and delivery times by 25%',
    industry: 'Logistics',
    result: '32% cost savings',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Chatbot for Customer Service Excellence', 
    desc: 'Deployed LLM-powered chatbot handling 80% of queries automatically with 92% customer satisfaction',
    industry: 'E-commerce',
    result: '80% automation rate',
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=1200&q=80&auto=format&fit=crop' 
  },
  { 
    title: 'Recommendation Engine for Streaming Platform', 
    desc: 'Personalized content recommendations increasing user engagement by 48% and watch time by 36%',
    industry: 'Media',
    result: '48% engagement boost',
    image: 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=1200&q=80&auto=format&fit=crop' 
  }
];

export default function CaseStudiesCarousel() {
  const swiperRef = React.useRef(null);
  const [isPaused, setIsPaused] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [announce, setAnnounce] = React.useState('');

  React.useEffect(() => {
    if (isPaused) return undefined;
    const id = setInterval(() => {
      if (swiperRef.current) swiperRef.current.slideNext();
    }, 4500);
    return () => clearInterval(id);
  }, [isPaused]);

  React.useEffect(() => setAnnounce(items[index].title), [index]);

  return (
    <Box sx={(theme) => ({ 
      py: 4, 
      position: 'relative',
      '& .swiper': {
        paddingBottom: '50px'
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 48,
        height: 48,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        boxShadow: theme.shadows[3],
        '&:after': {
          fontSize: '20px',
          fontWeight: 'bold'
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.dark
        }
      },
      '& .swiper-pagination-bullet': {
        width: 12,
        height: 12,
        backgroundColor: theme.palette.grey[400],
        opacity: 0.8
      },
      '& .swiper-pagination-bullet-active': {
        backgroundColor: theme.palette.primary.main,
        width: 24,
        borderRadius: 6
      }
    })}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        spaceBetween={30}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        breakpoints={{
          640: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 }
        }}
        onSwiper={s => (swiperRef.current = s)}
        onSlideChange={(s) => setIndex(s.activeIndex)}
      >
        {items.map((it, i) => (
          <SwiperSlide key={i}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={it.image}
                  alt={it.title}
                  sx={{ 
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <Chip 
                    label={it.industry} 
                    size="small" 
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1.5,
                    lineHeight: 1.3,
                    minHeight: 48
                  }}
                >
                  {it.title}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    lineHeight: 1.6,
                    minHeight: 60
                  }}
                >
                  {it.desc}
                </Typography>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha('#667eea', 0.08),
                    mb: 2
                  }}
                >
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'success.dark'
                    }}
                  >
                    {it.result}
                  </Typography>
                </Box>

                <Button 
                  size="small" 
                  variant="outlined"
                  fullWidth
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  View Case Study
                </Button>
              </CardContent>
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>

      <Box component="div" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)', whiteSpace: 'nowrap' }}>{announce}</Box>
    </Box>
  );
}
